import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, ChevronDown, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PrivacyConsent } from '@/components/PrivacyConsent';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function PatientChatWidget({ bookingLinkId, patientName, patientPhone, ownerId, isClinic = false }) {
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  // Intro form for clinic chat
  const [introNeeded, setIntroNeeded] = useState(!!ownerId && !patientName);
  const [introName, setIntroName] = useState(patientName || '');
  const [introPhone, setIntroPhone] = useState(patientPhone || '');
  const [introConsent, setIntroConsent] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevCountRef = useRef(0);
  const initialLoadDone = useRef(false);

  const patientToken = bookingLinkId || (thread?.id ? `clinic_${thread.id}` : '');

  const initThread = useCallback(async () => {
    if (!bookingLinkId && !ownerId) return;
    if (ownerId && introNeeded) return;
    try {
      const params = new URLSearchParams();
      if (bookingLinkId) params.append('booking_link_id', bookingLinkId);
      if (ownerId) params.append('owner_id', ownerId);
      const name = introName || patientName;
      const phone = introPhone || patientPhone;
      if (name) params.append('patient_name', name);
      if (phone) params.append('patient_phone', phone);
      const res = await fetch(`${API}/chat/threads?${params}`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (res.ok) {
        const data = await res.json();
        setThread(data);
      }
    } catch {}
  }, [bookingLinkId, ownerId, patientName, patientPhone, introName, introPhone, introNeeded]);

  const fetchMessages = useCallback(async () => {
    if (!thread?.id || (!bookingLinkId && !ownerId)) return;
    try {
      const token = bookingLinkId || `clinic_${thread.id}`;
      const res = await fetch(`${API}/chat/threads/${thread.id}/messages?patient_token=${token}`, {
        credentials: 'same-origin',
      });
      if (res.ok) {
        const data = await res.json();
        const msgs = Array.isArray(data) ? data : [];
        // On first load: set baseline count, no unread badge
        if (!initialLoadDone.current) {
          initialLoadDone.current = true;
          prevCountRef.current = msgs.length;
        } else if (!open) {
          // Only count new messages from staff (not patient's own) as unread
          const newStaffMsgs = msgs.slice(prevCountRef.current).filter(m => m.sender_type !== 'patient');
          if (newStaffMsgs.length > 0) {
            setUnread(prev => prev + newStaffMsgs.length);
          }
        }
        setMessages(msgs);
        if (open) prevCountRef.current = msgs.length;
      }
    } catch {}
  }, [thread, bookingLinkId, ownerId, open]);

  useEffect(() => { initThread(); }, [initThread]);

  useEffect(() => {
    if (thread) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [thread, fetchMessages]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      prevCountRef.current = messages.length;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open, messages.length]);

  const sendMessage = async () => {
    if (!text.trim() || !thread || sending) return;
    setSending(true);
    try {
      const token = bookingLinkId || `clinic_${thread.id}`;
      const senderName = introName || patientName || 'Посетитель';
      const res = await fetch(`${API}/chat/threads/${thread.id}/messages?patient_token=${token}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim(), sender_name: senderName }),
      });
      if (res.ok) {
        setText('');
        fetchMessages();
      }
    } catch {}
    setSending(false);
  };

  const uploadFile = async (file) => {
    if (!thread || !file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = bookingLinkId || `clinic_${thread.id}`;
      await fetch(`${API}/chat/threads/${thread.id}/upload?patient_token=${token}`, {
        method: 'POST', credentials: 'same-origin', body: fd,
      });
      fetchMessages();
    } catch {}
  };

  if (!bookingLinkId && !ownerId) return null;

  const handleIntroSubmit = () => {
    if (!introName.trim() || !introPhone.trim()) return;
    setIntroNeeded(false);
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button data-testid="patient-chat-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-amber-600 hover:bg-amber-500 text-white shadow-lg flex items-center justify-center transition-all hover:scale-105">
          <MessageCircle size={24} />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div data-testid="patient-chat-panel"
          className="fixed bottom-6 right-6 z-50 w-[360px] h-[500px] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-stone-700/60"
          style={{ background: '#1a1a1a' }}>
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between border-b border-stone-800 bg-stone-900/80">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-amber-600/20 flex items-center justify-center">
                <MessageCircle size={16} className="text-amber-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-stone-200">{isClinic ? 'Чат с клиникой' : 'Чат со специалистом'}</p>
                <p className="text-[10px] text-stone-500">
                  {thread?.mode === 'doctor' ? 'Вы общаетесь с врачом' : 'Ответит администратор'}
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-stone-500 hover:text-stone-300 p-1">
              <X size={18} />
            </button>
          </div>

          {/* Intro form for clinic chat */}
          {introNeeded ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 space-y-4">
              <MessageCircle size={32} className="text-amber-500 opacity-50" />
              <p className="text-sm text-stone-300 text-center">Представьтесь, чтобы начать чат</p>
              <input data-testid="chat-intro-name" value={introName} onChange={e => setIntroName(e.target.value)}
                placeholder="Ваше имя" className="w-full px-3 py-2 rounded-lg bg-stone-800 border border-stone-700 text-stone-200 text-sm placeholder:text-stone-600" />
              <input data-testid="chat-intro-phone" value={introPhone} onChange={e => setIntroPhone(e.target.value)}
                placeholder="Телефон" className="w-full px-3 py-2 rounded-lg bg-stone-800 border border-stone-700 text-stone-200 text-sm placeholder:text-stone-600" />
              <PrivacyConsent checked={introConsent} onChange={setIntroConsent} />
              <button data-testid="chat-intro-submit" onClick={handleIntroSubmit}
                disabled={!introName.trim() || !introPhone.trim() || !introConsent}
                className="w-full py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium disabled:opacity-40 transition-colors">
                Начать чат
              </button>
            </div>
          ) : (
          <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {messages.length === 0 ? (
              <div className="text-center py-10 text-stone-600 text-sm">
                <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
                <p>Напишите сообщение, и мы ответим!</p>
              </div>
            ) : (
              messages.map(m => (
                <div key={m.id} className={`flex ${m.sender_type === 'patient' ? 'justify-end' : 'justify-start'}`}>
                  <div data-testid={`chat-msg-${m.id}`}
                    className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                      m.sender_type === 'patient'
                        ? 'bg-amber-600 text-white rounded-br-sm'
                        : m.sender_type === 'system'
                        ? 'bg-stone-800/50 text-stone-500 text-xs italic text-center w-full max-w-full rounded-lg'
                        : 'bg-stone-800 text-stone-200 rounded-bl-sm'
                    }`}>
                    {m.sender_type !== 'patient' && m.sender_type !== 'system' && (
                      <p className="text-[10px] font-medium text-amber-500/70 mb-0.5">{m.sender_name}</p>
                    )}
                    <p className="whitespace-pre-wrap break-words">{m.attachment ? '' : m.text}</p>
                    {m.attachment && (
                      <a href={`${API}/chat/attachments/${m.attachment.filename}?patient_token=${bookingLinkId || `clinic_${thread?.id}`}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-[11px] underline underline-offset-2 opacity-80 hover:opacity-100 mt-0.5">
                        <Paperclip size={10} /> {m.attachment.original_name}
                      </a>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-stone-800 bg-stone-900/50">
            <form onSubmit={e => { e.preventDefault(); sendMessage(); }} className="flex gap-2 items-center">
              <label className="shrink-0 cursor-pointer text-stone-600 hover:text-stone-400 flex items-center justify-center w-8 h-8 rounded-full hover:bg-stone-800 transition-colors">
                <Paperclip size={16} />
                <input type="file" className="hidden" onChange={e => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value = ''; }} />
              </label>
              <input ref={inputRef}
                data-testid="patient-chat-input"
                value={text} onChange={e => setText(e.target.value)}
                placeholder="Сообщение..."
                className="flex-1 bg-stone-800 border border-stone-700 rounded-full px-4 py-2 text-sm text-stone-200 placeholder:text-stone-600 focus:outline-none focus:border-amber-700/60"
              />
              <button data-testid="patient-chat-send" type="submit" disabled={!text.trim() || sending}
                className="w-9 h-9 rounded-full bg-amber-600 hover:bg-amber-500 disabled:opacity-30 text-white flex items-center justify-center shrink-0 transition-colors">
                <Send size={16} />
              </button>
            </form>
          </div>
          </>
          )}
        </div>
      )}
    </>
  );
}
